import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetch } from "@/src/lib/fetch";
import { cn } from "../../../lib/utils";
import { XeroInvoiceSearchDialog } from "../XeroInvoiceSearchDialog";
import { PaymentStatePill, PayViaRevolutDialog, type SupplierPaymentView } from "../../Finance/PayViaRevolutDialog";

/**
 * Live Xero state for the booking's linked invoices & bills, presented the way
 * the ops CA booking page does it: one tile per document with the status and
 * Total/Paid/Due read from Xero at view time, plus a combined payments-made
 * history. Falls back to the last-synced snapshot (flagged) when Xero is
 * unreachable.
 *
 * Documents are GROUPED by what they actually are, because the money means
 * different things: a referral commission owed to a travel agent is an ACCPAY
 * but is not a supplier charter bill, and a HELM commission invoice is an
 * ACCREC but is not client charter income. Both sit in their own groups and
 * are excluded from the charter margin. Voided/deleted/draft documents never
 * appear at all.
 */

type XeroDoc = {
  invoiceId: string;
  type: string;
  live: boolean;
  number: string | null;
  reference: string | null;
  status: string | null;
  contactName: string | null;
  date: string | null;
  dueDate: string | null;
  total: number;
  amountPaid: number;
  amountDue: number;
  currency: string | null;
  brand: string | null;
  accountCodes: string[];
  lineCount: number;
  category: string;
  categoryLabel: string;
  group: "client_charter" | "supplier_charter" | "extras" | "commission_out" | "commission_in";
  categoryOverridden: boolean;
  payments: { paymentId: string; date: string | null; amount: number; reference: string | null }[];
  xeroUrl: string;
};

type Economics = {
  charterIn: number;
  charterOut: number;
  margin: number;
  marginPct: number | null;
  clientPaid: number;
  supplierPaid: number;
  commissionOut: number;
  commissionIn: number;
  currency: string | null;
  mixedCurrency: boolean;
  /** Foreign-currency documents were converted into `currency` at today's rate. */
  converted?: boolean;
  fxNotes?: string[];
  /** Commission can be in a different currency from the charter. */
  commissionCurrency: string | null;
};

type BookingXero = {
  bookingId: string;
  accountCode: string | null;
  brand: string | null;
  clientName: string | null;
  operatorName?: string | null;
  economics: Economics;
  docs: XeroDoc[];
  unallocatedPayments: { id: string; source: string; amount: number; currency: string | null; occurredAt: string | null; reference: string | null }[];
  supplierPayments?: SupplierPaymentView[];
};

const fmtMoney = (v: number, ccy: string | null) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: ccy || "EUR", maximumFractionDigits: 0 }).format(v);
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const statusTone = (status: string | null) => {
  const s = (status ?? "").toUpperCase();
  if (s === "PAID") return "bg-green-100 text-green-800";
  if (s === "AUTHORISED") return "bg-blue-100 text-blue-800";
  if (s === "VOIDED" || s === "DELETED" || s === "DRAFT") return "bg-slate-100 text-slate-500";
  return "bg-slate-100 text-slate-600";
};

function MoneyCell({ label, value, ccy, tone }: { label: string; value: number; ccy: string | null; tone?: "warn" | "ok" }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={cn("text-sm font-semibold tabular-nums", tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-green-700" : "text-slate-800")}>
        {fmtMoney(value, ccy)}
      </p>
    </div>
  );
}

const GROUPS: { key: XeroDoc["group"]; title: string; blurb: string }[] = [
  { key: "client_charter", title: "Client invoices", blurb: "charter money in" },
  { key: "supplier_charter", title: "Supplier bills", blurb: "operator cost" },
  { key: "extras", title: "Extras & APA", blurb: "pass-through" },
  { key: "commission_out", title: "Referral commission", blurb: "owed to an agent — not a supplier bill, excluded from margin" },
  { key: "commission_in", title: "HELM commission invoices", blurb: "our own revenue — excluded from margin" },
];

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "charter", label: "Charter" },
  { value: "extras_apa", label: "Extras / APA" },
  { value: "referral_commission", label: "Referral commission (to agent)" },
  { value: "commission_helm", label: "HELM commission invoice" },
];

function DocTile({ doc, onRetag, onPay, payment }: { doc: XeroDoc; onRetag: (doc: XeroDoc, category: string) => void; onPay?: (doc: XeroDoc) => void; payment?: SupplierPaymentView | null }) {
  const isBill = doc.type === "ACCPAY";
  const accent =
    doc.group === "commission_out" || doc.group === "commission_in"
      ? "border-l-violet-500"
      : doc.group === "extras"
        ? "border-l-sky-500"
        : isBill
          ? "border-l-amber-500"
          : "border-l-emerald-600";
  return (
    <div className={cn("min-w-[280px] flex-1 rounded-lg border border-slate-200 border-l-[3px] bg-white p-3", accent)}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
          {isBill ? "Bill" : "Invoice"}
          {doc.brand && <span className="ml-1 normal-case">· {doc.brand}</span>}
        </span>
        <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", statusTone(doc.status))}>{doc.status ?? "—"}</span>
      </div>
      <p className="mt-1 text-sm">
        <span className="font-semibold">{doc.number ?? `${doc.invoiceId.slice(0, 8)}…`}</span>
        {doc.contactName && <span className="ml-1.5 text-xs text-slate-500">· {doc.contactName}</span>}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <MoneyCell label="Total" value={doc.total} ccy={doc.currency} />
        <MoneyCell label="Paid" value={doc.amountPaid} ccy={doc.currency} tone={doc.amountPaid > 0 ? "ok" : undefined} />
        <MoneyCell label="Due" value={doc.amountDue} ccy={doc.currency} tone={doc.amountDue > 0 ? "warn" : "ok"} />
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">
        {doc.date && <>Issued {fmtDate(doc.date)}</>}
        {doc.date && doc.dueDate && " · "}
        {doc.dueDate && <>Due {fmtDate(doc.dueDate)}</>}
        {doc.accountCodes.length > 0 && <> · Account{doc.accountCodes.length > 1 ? "s" : ""} {doc.accountCodes.join(", ")}</>}
        {doc.lineCount > 1 && <> · {doc.lineCount} lines</>}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <a
          href={doc.xeroUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-md border px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-blue-400 hover:text-blue-700"
        >
          Open in Xero
        </a>
        {isBill && doc.amountDue > 0 && onPay && (
          payment && !["payment_failed", "draft_rejected", "cancelled"].includes(payment.state) ? (
            <button type="button" onClick={() => onPay(doc)} title={payment.error ?? "Open the Revolut payment"}><PaymentStatePill state={payment.state} /></button>
          ) : (
            <button type="button" onClick={() => onPay(doc)} className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800" title="Create a Revolut payment draft for this bill">Pay via Revolut</button>
          )
        )}
        {!doc.live && (
          <span className="text-[11px] text-amber-600" title="Xero was unreachable — showing the last replicated snapshot.">
            last synced state
          </span>
        )}
      </div>
      <label className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
        <span className="uppercase tracking-wide text-slate-400">Type</span>
        <select
          value={doc.category}
          onChange={(e) => onRetag(doc, e.target.value)}
          title={
            doc.categoryOverridden
              ? "Set here — the ops sync won't change it"
              : "Change how this document is treated in the margin"
          }
          className="flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 outline-none hover:border-slate-400 focus:border-blue-400"
        >
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {doc.categoryOverridden && <span className="text-slate-400" title="Manually set">·set</span>}
      </label>
    </div>
  );
}

/** Charter in / out / margin — commission documents deliberately excluded and
 *  reported separately so the margin reflects the charter itself. */
function EconomicsPanel({ economics: e }: { economics: Economics }) {
  if (e.charterIn === 0 && e.charterOut === 0) return null;
  const ccy = e.currency;
  const Cell = ({ label, value, tone, hint }: { label: string; value: number; tone?: string; hint?: string }) => (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={cn("text-base font-semibold tabular-nums", tone)}>{fmtMoney(value, ccy)}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Cell label="Charter in" value={e.charterIn} hint={`${fmtMoney(e.clientPaid, ccy)} collected`} />
        <Cell label="Charter out" value={e.charterOut} hint={`${fmtMoney(e.supplierPaid, ccy)} paid`} />
        <Cell
          label="Margin"
          value={e.margin}
          tone={e.margin > 0 ? "text-green-700" : e.margin < 0 ? "text-red-600" : undefined}
          hint={e.marginPct != null ? `${e.marginPct}% of charter in` : undefined}
        />
        {(e.commissionOut > 0 || e.commissionIn > 0) && (
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Commission</p>
            <p className="text-base font-semibold tabular-nums text-violet-700">
              {fmtMoney(e.commissionIn - e.commissionOut, e.commissionCurrency ?? ccy)}
            </p>
            <p className="text-[11px] text-slate-400">
              {e.commissionOut > 0 && `${fmtMoney(e.commissionOut, e.commissionCurrency ?? ccy)} to agent`}
              {e.commissionOut > 0 && e.commissionIn > 0 && " · "}
              {e.commissionIn > 0 && `${fmtMoney(e.commissionIn, e.commissionCurrency ?? ccy)} invoiced`}
              {" — outside margin"}
            </p>
          </div>
        )}
      </div>
      {e.converted && (
        <p className="mt-2 text-[11px] text-slate-500">
          Documents in more than one currency — converted to {e.currency} at today's rate ({(e.fxNotes ?? []).join(", ")}); the per-document tiles show the original amounts.
        </p>
      )}
      {e.mixedCurrency && !e.converted && (
        <p className="mt-2 text-[11px] text-amber-700">
          This booking has documents in more than one currency and no rate is available to convert them — the totals above add them as-is.
        </p>
      )}
    </div>
  );
}

export function XeroSection({ bookingId }: { bookingId: string }) {
  const [data, setData] = useState<BookingXero | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [finding, setFinding] = useState(false);
  // What the Find dialog opens on: "ACCPAY" from the Supplier bills section, else everything.
  const [findType, setFindType] = useState<"all" | "ACCREC" | "ACCPAY">("all");
  const [paying, setPaying] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const load = useCallback(() => {
    setLoading(true);
    fetch<{ data: BookingXero }>(`/api/finance/bookings/${bookingId}/xero`)
      .then((r) => {
        setData(r.data);
        // A schedule was just mirrored from Xero — the header bars and payment
        // sections read their own queries, so refresh them.
        const s: any = (r.data as any).scheduleSync;
        if (s && (s.customer?.steps || s.operator?.steps)) queryClient.invalidateQueries();
      })
      .catch((e: any) => {
        // Roles without finance access simply don't get the section.
        if (e?.status === 403 || e?.status === 401) setHidden(true);
        else setError(e?.message ?? "Failed to load Xero records.");
      })
      .finally(() => setLoading(false));
  }, [bookingId]);
  useEffect(load, [load]);

  // Re-tagging changes which group a document sits in and whether it counts
  // toward margin, so reload rather than patching locally.
  const retag = (doc: XeroDoc, category: string) => {
    fetch(`/api/finance/invoices/${doc.invoiceId}/category`, "POST", { category })
      .then(() => load())
      .catch((e: any) => setError(e?.message ?? "Couldn't change the category."));
  };

  const history = useMemo(() => {
    if (!data) return [];
    const rows: { key: string; date: string | null; side: "in" | "out"; amount: number; currency: string | null; source: string; reference: string | null }[] = [];
    for (const d of data.docs) {
      const side = d.type === "ACCPAY" ? ("out" as const) : ("in" as const);
      const label = d.type === "ACCPAY" ? "Supplier bill" : "Client invoice";
      for (const p of d.payments) {
        rows.push({
          key: `${d.invoiceId}-${p.paymentId}`,
          date: p.date,
          side,
          amount: p.amount,
          currency: d.currency,
          source: `${label} ${d.number ?? ""}`.trim(),
          reference: p.reference,
        });
      }
    }
    return rows.sort((a, b) => String(b.date ?? "").localeCompare(String(a.date ?? "")));
  }, [data]);

  if (hidden) return null;
  if (loading) return <p className="text-sm text-slate-400">Reading Xero…</p>;
  if (error) return <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>;
  if (!data) return null;

  const totalIn = history.filter((r) => r.side === "in").reduce((s, r) => s + r.amount, 0);
  const totalOut = history.filter((r) => r.side === "out").reduce((s, r) => s + r.amount, 0);
  const ccy = data.docs[0]?.currency ?? "EUR";

  const findButton = (
    <button
      type="button"
      onClick={() => { setFindType("all"); setFinding(true); }}
      className="rounded-md border px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-blue-400 hover:text-blue-700"
      title="Search Xero by client name, invoice number or reference and link the invoice to this booking"
    >
      Find invoice in Xero…
    </button>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {data.accountCode ? (
          <p className="text-sm text-slate-500">
            Charter account <span className="font-medium text-slate-700">#{data.accountCode}</span>
          </p>
        ) : <span />}
        {data.docs.length > 0 && findButton}
      </div>

      {data.docs.length === 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-sm text-amber-800">
            No Xero invoice is linked to this booking. If one exists in Xero already, find it and link it here; otherwise it appears once the booking is confirmed.
          </p>
          {findButton}
        </div>
      ) : (
        <>
          <EconomicsPanel economics={data.economics} />
          {GROUPS.map((g) => {
            const docs = data.docs.filter((d) => d.group === g.key);
            // A booking with a client invoice but no supplier bill still needs a way to
            // find and link the bill (raised by hand, or under another reference).
            if (!docs.length && g.key === "supplier_charter") {
              return (
                <div key={g.key} className="flex flex-col gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{g.title}<span className="ml-2 font-normal normal-case tracking-normal text-slate-400">{g.blurb}</span></p>
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2">
                    <p className="text-sm text-slate-500">No supplier bill is linked. Check booking raises one; if the bill already exists in Xero, find it and link it here.</p>
                    <button type="button" onClick={() => { setFindType("ACCPAY"); setFinding(true); }} className="rounded-md border px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-blue-400 hover:text-blue-700" title="Search the Xero org's bills by supplier name, bill number or reference and link the bill to this booking">Find supplier bill in Xero…</button>
                  </div>
                </div>
              );
            }
            if (!docs.length) return null;
            return (
              <div key={g.key} className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  {g.title}
                  <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">{g.blurb}</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {docs.map((d) => (
                    <DocTile key={d.invoiceId} doc={d} onRetag={retag} onPay={(doc) => setPaying(doc.invoiceId)} payment={(data.supplierPayments ?? []).find((p) => p.invoiceId === d.invoiceId) ?? null} />
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}

      {data.unallocatedPayments.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {data.unallocatedPayments.length} matched payment{data.unallocatedPayments.length > 1 ? "s" : ""} (
          {data.unallocatedPayments.map((p) => fmtMoney(p.amount, p.currency)).join(", ")}) not yet allocated to a Xero
          invoice — allocate from the payments inbox.
        </div>
      )}

      {paying && <PayViaRevolutDialog invoiceId={paying} onClose={() => setPaying(null)} onDone={() => { load(); queryClient.invalidateQueries(); }} />}
      {finding && (
        <XeroInvoiceSearchDialog
          bookingId={bookingId}
          bookingLabel={data.clientName}
          brand={data.brand}
          initialQuery={findType === "ACCPAY" ? (data.operatorName ?? null) : (data.clientName?.split(/\s+/).pop() ?? null)}
          initialType={findType}
          onClose={() => setFinding(false)}
          onLinked={() => { setFinding(false); load(); queryClient.invalidateQueries(); }}
        />
      )}

      {history.length > 0 && (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <p className="text-sm font-medium text-slate-700">Payments made</p>
            <p className="text-xs text-slate-400">
              Received {fmtMoney(totalIn, ccy)} · Sent {fmtMoney(totalOut, ccy)}
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Side</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Reference</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="whitespace-nowrap px-3 py-2">{fmtDate(r.date)}</td>
                  <td className="px-3 py-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", r.side === "in" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800")}>
                      {r.side === "in" ? "In" : "Out"}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{fmtMoney(r.amount, r.currency)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.source}</td>
                  <td className="max-w-[260px] px-3 py-2 text-slate-500">
                    <span className="line-clamp-1">{r.reference ?? "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
